import { useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, Loader2 } from "lucide-react";
import { getLegalDoc } from "@/lib/legal";
import { DocBlocks } from "@/components/legal/DocBlocks";
import { inputCls, labelCls, FieldError, CheckRow, money } from "./ui";

function MasterInline() {
  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState(null);
  const toggle = () => {
    setOpen((o) => !o);
    if (!doc) getLegalDoc("participant-agreement").then(setDoc).catch(() => setDoc({ blocks: [] }));
  };
  return (
    <div className="mt-3">
      <button type="button" data-testid="master-agreement-toggle" onClick={toggle} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#60a5fa]">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {open ? "Hide" : "Read here"}
      </button>
      {open && (
        <div data-testid="master-agreement-inline" className="mt-3 max-h-72 overflow-y-auto rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
          {!doc ? <Loader2 size={18} className="animate-spin text-white/40" /> : <DocBlocks blocks={doc.blocks} compact />}
        </div>
      )}
    </div>
  );
}

function AgreementCard({ letter, title, meta, children }) {
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 md:p-6">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#60a5fa]">Agreement {letter} · Required</p>
      <h3 className="mt-2 text-lg font-extrabold tracking-tight text-white leading-snug">{title}</h3>
      {meta && <p className="mt-1 text-xs font-semibold text-white/40">{meta}</p>}
      {children}
    </section>
  );
}

export default function StepAgreements({ master, agreements, form, set, accept, setAccept, errors }) {
  const participant = `${form.first_name} ${form.last_name}`.trim();
  return (
    <div className="mt-6 space-y-5">
      <AgreementCard letter="A" title={`Master Agreement: ${master.title}`} meta={`Version ${master.version} · Effective Date ${master.effective_date}`}>
        <p className="mt-3 text-sm font-medium text-white/55">The standard terms for all Rep Lab programs: fees, withdrawals and credits, conduct, health and emergency care, assumption of risk, and release of claims.</p>
        <a href={`/legal/${master.slug}`} target="_blank" rel="noreferrer" data-testid="master-agreement-link" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#60a5fa] underline">
          Open the complete agreement <ExternalLink size={13} />
        </a>
        <MasterInline />
        <div className="mt-4">
          <CheckRow testid="master-agreement-checkbox" checked={!!accept.master} onChange={(v) => setAccept((a) => ({ ...a, master: v }))} error={errors.master}
            ariaLabel={`I accept the ${master.title}, version ${master.version}`} dataAttrs={{ "data-agreement": master.slug, "data-version": master.version }}>
            I accept this agreement.
          </CheckRow>
        </div>
      </AgreementCard>

      {agreements.map((a) => {
        const d = Object.fromEntries(a.details.map((x) => [x.key, x.value]));
        return (
          <AgreementCard key={a.program_id} letter="B" title={`Program Enrollment Agreement — ${a.program_title}`} meta={`Template version ${a.template.version} · ${d.price || ""}`}>
            <div data-testid={`enrollment-agreement-${a.program_id}`} className="mt-4 max-h-80 overflow-y-auto rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
              {a.header.map((h, i) => <p key={i} className={i === 1 ? "text-base font-extrabold text-white" : "text-[11px] font-bold uppercase tracking-wider text-white/40"}>{h}</p>)}
              <DocBlocks blocks={a.blocks} compact emptyText="Recorded when you accept" />
            </div>
            <div className="mt-4">
              <CheckRow testid={`enrollment-accept-${a.program_id}`} checked={!!accept[a.program_id]} onChange={(v) => setAccept((s) => ({ ...s, [a.program_id]: v }))} error={errors[`ea_${a.program_id}`]}
                ariaLabel={`I accept the Program Enrollment Agreement for ${a.program_title}, version ${a.template.version}`} dataAttrs={{ "data-agreement": `enrollment-${a.program_id}`, "data-version": a.template.version }}>
                I accept this agreement.
              </CheckRow>
            </div>
          </AgreementCard>
        );
      })}

      <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 md:p-6">
        <label className={labelCls} htmlFor="reg-signature">Guardian electronic signature *</label>
        <p className="mt-1 text-xs font-medium text-white/45">Type your full legal name exactly as entered for the parent / legal guardian ({form.guardian_name}).</p>
        <input id="reg-signature" data-testid="reg-signature" value={form.signature} onChange={(e) => set("signature", e.target.value)} placeholder="Type your full name"
          className={`${inputCls} mt-3 font-semibold italic ${errors.signature ? "border-red-400/60" : ""}`} autoComplete="off" />
        <FieldError msg={errors.signature} />
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 md:p-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/45">Optional · Photo, Video & Media Authorization</p>
        <p className="mt-2 text-sm font-medium text-white/55">Separate, voluntary choice under Section 12 of the <a href={`/legal/${master.slug}#section-12`} target="_blank" rel="noreferrer" className="font-bold text-[#60a5fa] underline">Participant Service Agreement</a>. Declining does not affect program eligibility.</p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button type="button" data-testid="reg-media-yes" onClick={() => set("media_consent", "agree")}
            className={`rounded-2xl border-2 px-4 py-3 text-left text-sm font-semibold transition-colors ${form.media_consent === "agree" ? "border-[#2563EB] bg-[#2563EB]/10 text-white" : "border-white/10 text-white/70 hover:border-white/25"}`}>
            I authorize photo and video use.
          </button>
          <button type="button" data-testid="reg-media-no" onClick={() => set("media_consent", "decline")}
            className={`rounded-2xl border-2 px-4 py-3 text-left text-sm font-semibold transition-colors ${form.media_consent === "decline" ? "border-[#2563EB] bg-[#2563EB]/10 text-white" : "border-white/10 text-white/70 hover:border-white/25"}`}>
            I do not authorize photo and video use.
          </button>
        </div>
        <FieldError msg={errors.media_consent} />
        <label className="mt-4 flex items-start gap-3 text-sm font-medium text-white/70">
          <input type="checkbox" data-testid="reg-leaderboard-consent" checked={!!form.leaderboard_consent} onChange={(e) => set("leaderboard_consent", e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#2563EB]" />
          <span>Optional: I allow Rep Lab to show my athlete's name and training scores on the public Rep Lab Leaderboard. No contact, birth date or medical details are ever shown. I can withdraw this anytime by emailing info@replabbaseball.com.</span>
        </label>
      </section>
    </div>
  );
}
